import React, { ReactNode, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Global } from '@emotion/react';
// material
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { APPEARANCE_MODE_DARK, APPEARANCE_MODE_LIGHT } from './constants';

import { getCssVarsFromTheme, getTheme } from './helpers';
import * as commonSelectors from '@/modules/common/redux/selectors/common';

import componentsOverride from './components/MUIOverrides';

type ThemeConfigProps = {
  children: ReactNode;
};

export type ThemeModeType = typeof APPEARANCE_MODE_LIGHT | typeof APPEARANCE_MODE_DARK;

function ThemeConfig(props: ThemeConfigProps) {
  const appearance = useSelector(commonSelectors.appearanceSelector) as ThemeModeType;
  const font = useSelector(commonSelectors.fontSelector) as string;
  const color = useSelector(commonSelectors.colorSelector) as string;
  const bg = useSelector(commonSelectors.bgSelector) as string;
  const configuration = useSelector(commonSelectors.configurationSelector);

  const [theme, themeCssProps] = useMemo(() => {
    const theme$ = createTheme(getTheme(appearance, color, bg, configuration));

    const themeCssProps$ = getCssVarsFromTheme(theme$);

    theme$.components = componentsOverride(theme$);

    return [theme$, themeCssProps$];
  }, [appearance, bg, font, color]);

  return (
    <ThemeProvider theme={theme}>
      <Global
        styles={{
          ':host': {
            ...themeCssProps,
            '--general-font-family': font,
          },
        }}
      />
      {props.children}
    </ThemeProvider>
  );
}

ThemeConfig.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeConfig;
